import {
  Children,
  isValidElement,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { QueryErrorNote } from './error-panel';
import { Icons } from './primitives';
import { createPortal } from 'react-dom';
import { useSlot } from '@/lib/responsive';
import { ResponsivePicker } from './responsive-picker';
import {
  cells,
  mobileSpec,
  resolveBodyState,
  textLabel,
  type DataTableProps,
  type VtColumn,
} from './data-table';

/**
 * The phone presentation of a `createDataTable` screen: the same rows, filters
 * and handlers, rendered as one card per row.
 *
 * What a card shows is declared on the columns (`VtColumn.mobile`): the title,
 * an optional subtitle and status chip, and up to four stats in a 2×2 grid
 * (`mobileStats` raises that, two per row).
 * Everything else is left to the row's detail view — the whole card is the
 * tap target for it, with an icon button as the keyboard and screen-reader
 * path. Cards are measured, never given a fixed height, so a long title only
 * makes its own card taller.
 */
/** Whether a cell's content carries a link (an `<a>` or a router `Link`, which renders one). */
function cellHasLink(node: ReactNode): boolean {
  return Children.toArray(node).some((child) => {
    if (!isValidElement<{ children?: ReactNode; to?: unknown; href?: unknown }>(child))
      return false;
    if (child.type === 'a' || child.props.to != null || child.props.href != null) return true;
    return cellHasLink(child.props.children);
  });
}

export function MobileRecords<T extends { id: string | number }>({
  columns,
  rows,
  unfilteredTotal,
  deps = [],
  empty,
  noMatch = 'Nothing matches these filters.',
  loading,
  renderRow,
  onRowClick,
  rowStyle,
  rowClassName,
  mobileStats = 4,
}: DataTableProps<T> & { columns: readonly VtColumn[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 190,
    getItemKey: (index) => rows[index]!.id,
    overscan: 4,
  });
  const state = resolveBodyState({ deps, total: rows.length, unfilteredTotal });
  // The sort button and count sit on the filter bar's row when the screen has one.
  const toolsSlot = useSlot('.mobile-filter-bar-tools', true);
  // A fresh list starts at its top whatever the scroller held before this
  // mount (a restored position, a focus scroll): card 1 is never half hidden.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  const indexed = columns.map((col, index) => ({ col, index, spec: mobileSpec(col) }));
  const role = (c: VtColumn) => mobileSpec(c).role;
  const titleIndex = indexed.find(({ col }) => role(col) === 'title')?.index ?? 0;
  const subtitle = indexed.filter(
    ({ col, index }) => role(col) === 'subtitle' && index !== titleIndex,
  );
  const status = indexed.find(({ col, index }) => role(col) === 'status' && index !== titleIndex);
  // A joined stat lives in its host's cell, so it takes no grid slot of its own.
  const joined = indexed.filter(({ spec }) => spec.role === 'stat' && spec.join);
  // Declaration order, then every stat with an explicit `order` moves to that position.
  const declared = indexed.filter(
    ({ col, index, spec }) => role(col) === 'stat' && !spec.join && index !== titleIndex,
  );
  const stats = declared.filter(({ spec }) => spec.order == null);
  for (const item of declared
    .filter(({ spec }) => spec.order != null)
    .sort((a, b) => a.spec.order! - b.spec.order!))
    stats.splice(Math.max(0, Math.min(stats.length, item.spec.order! - 1)), 0, item);
  stats.splice(mobileStats);
  // Only a header that is itself a control (a sort button) can sort; a plain
  // text header has nothing to offer a sort sheet.
  // A column declares itself sortable by carrying the key at all; the value
  // says whether it is the active sort (`undefined` or 'none' when it is not).
  const sortable = indexed.filter(({ col }) => 'ariaSort' in col);
  const activeSort = sortable.find(
    ({ col }) => col.ariaSort === 'ascending' || col.ariaSort === 'descending',
  );

  const openRow = (row: T, event: MouseEvent<HTMLElement>) => {
    // Taps on inner controls keep their own meaning.
    if (
      (event.target as HTMLElement).closest('a,button,input,select,summary,details,[role=button]')
    )
      return;
    if (onRowClick) return onRowClick(row);
    event.currentTarget.querySelector<HTMLAnchorElement>('.mobile-record-title a')?.click();
  };
  // Text a cell renders when it has nothing to say; a subtitle made only of it is dropped.
  const isBlank = (node: ReactNode): boolean => {
    if (node == null || node === '' || node === false) return true;
    if (typeof node === 'string') return node.trim() === '' || node.trim() === '—';
    if (Array.isArray(node)) return node.every(isBlank);
    return false;
  };

  return (
    <div className="mobile-records" ref={scrollRef} role="region" aria-label="Records" tabIndex={0}>
      {(() => {
        const tools = (
          <div className="mobile-record-tools">
            {sortable.length > 0 && (
              <button
                type="button"
                className="btn"
                aria-haspopup="dialog"
                aria-expanded={sortOpen}
                onClick={() => setSortOpen(true)}
              >
                <span aria-hidden="true">↕</span>
                Sort
                {activeSort
                  ? `: ${textLabel(activeSort.col.header)} ${activeSort.col.ariaSort === 'ascending' ? '↑' : '↓'}`
                  : ''}
              </button>
            )}
            <span
              className="mobile-record-count"
              aria-label={`${rows.length.toLocaleString()} records`}
            >
              {rows.length.toLocaleString()}
            </span>
            {sortOpen && (
              <ResponsivePicker label="Sort records" onClose={() => setSortOpen(false)}>
                <div className="mobile-sort-options" onClick={() => setSortOpen(false)}>
                  {sortable.map(({ col }) => (
                    <div
                      key={col.key}
                      className="mobile-sort-option"
                      data-active={
                        col.ariaSort === 'ascending' || col.ariaSort === 'descending' || undefined
                      }
                    >
                      {col.header}
                    </div>
                  ))}
                </div>
              </ResponsivePicker>
            )}
          </div>
        );
        // Hold the tools until the slot lookup has run: rendering them inline
        // for one frame and then portaling them shifts the list under the reader.
        if (toolsSlot === undefined) return null;
        return toolsSlot ? createPortal(tools, toolsSlot) : tools;
      })()}
      {state.kind !== 'rows' ? (
        <div className="mobile-record-empty" role="status">
          {state.kind === 'loading' && (loading ?? `Loading ${state.what}…`)}
          {state.kind === 'error' && (
            <QueryErrorNote error={state.error} what={state.what} onRetry={state.retry} />
          )}
          {state.kind === 'empty' && empty}
          {state.kind === 'no-match' && noMatch}
        </div>
      ) : (
        <div
          role="list"
          aria-label="Records"
          style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}
        >
          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index]!;
            const fields = cells(renderRow(row, item.index));
            const content = (index: number): ReactNode => fields[index]?.props.children;
            // An empty stat still keeps its slot so the grid stays aligned, but says so.
            const stat = (index: number): ReactNode => {
              const value = content(index);
              return value == null || value === '' || value === false ? '—' : value;
            };
            const label = (col: VtColumn) =>
              mobileSpec(col).label ?? (textLabel(col.header) || col.key.replaceAll('_', ' '));
            // A host stat's value with every stat joined into it, in the order they ask for.
            const joinedStat = (host: VtColumn, index: number): ReactNode => {
              const parts = joined.filter(({ spec }) => spec.join!.with === host.key);
              if (parts.length === 0) return stat(index);
              const before = parts.filter(({ spec }) => spec.join!.order === 'before');
              const after = parts.filter(({ spec }) => spec.join!.order !== 'before');
              return (
                <>
                  {before.map(({ col, index: i, spec }) => (
                    <span key={col.key}>
                      {stat(i)}
                      <span className="mobile-record-join">{spec.join!.separator}</span>
                    </span>
                  ))}
                  {stat(index)}
                  {after.map(({ col, index: i, spec }) => (
                    <span key={col.key}>
                      <span className="mobile-record-join">{spec.join!.separator}</span>
                      {stat(i)}
                    </span>
                  ))}
                </>
              );
            };
            const rowTitle = content(titleIndex);
            const hasLink = !!(
              fields[titleIndex] && cellHasLink(fields[titleIndex]!.props.children)
            );
            const opens = !!onRowClick || hasLink;
            return (
              <div
                key={item.key}
                data-index={item.index}
                ref={virtualizer.measureElement}
                role="listitem"
                aria-posinset={item.index + 1}
                aria-setsize={rows.length}
                className="mobile-record-position"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${item.start}px)`,
                }}
              >
                <article
                  className={[
                    'mobile-record',
                    opens && 'mobile-record--tappable',
                    rowClassName?.(row),
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={rowStyle?.(row)}
                  onClick={(event) => openRow(row, event)}
                >
                  <div className="mobile-record-head">
                    <div className="mobile-record-identity">
                      <div className="mobile-record-title">{rowTitle}</div>
                      {subtitle.some(({ index }) => !isBlank(content(index))) && (
                        <div className="mobile-record-subtitle">
                          {subtitle
                            .filter(({ index }) => !isBlank(content(index)))
                            .map(({ col, index }) => (
                              <span key={col.key}>{content(index)}</span>
                            ))}
                        </div>
                      )}
                    </div>
                    {status && <div className="mobile-record-status">{content(status.index)}</div>}
                    {onRowClick ? (
                      <button
                        type="button"
                        className="mobile-record-open"
                        aria-label="Open details"
                        onClick={() => onRowClick(row)}
                      >
                        <span aria-hidden="true">{Icons.chev_r}</span>
                      </button>
                    ) : hasLink ? (
                      <span className="mobile-record-open" aria-hidden="true">
                        {Icons.chev_r}
                      </span>
                    ) : null}
                  </div>
                  {stats.length > 0 && (
                    <dl className="mobile-record-values">
                      {stats.map(({ col, index }) => (
                        <div key={col.key}>
                          <dt>{label(col)}</dt>
                          <dd>{joinedStat(col, index)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </article>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
